import React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth';
import CellLinkWithIcon from 'src/features/membership/components/cell/CellLinkWIthIcon';
import { PATH } from 'src/features/navigation/utils/constants';
import { useListPayStationEvents } from 'src/features/payment/api/useListPayStationEvents';
import { useListPayStations } from 'src/features/payment/api/useListPayStations';
import EventSidePanel from 'src/features/payment/components/EventSidePanel';
import {
    eventAPIBadgeOptions,
    eventCategoryBadgeOptions,
    eventTypeTranslationMap,
} from 'src/features/payment/utils/constants';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import { PayStationEvent } from 'src/features/payment/utils/types';
import { CellSidePanelActions } from 'src/features/visitor/components/cell/CellSidePanelActions';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import SidePanel from 'src/registry/components/sidePanel/SidePanel';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import { useSingleRowSelection } from 'src/registry/hooks/useSingleRowSelection';
import useSorting from 'src/registry/hooks/useSorting';
import { commonMessages } from 'src/utils/messages';

export const PAY_STATION_EVENTS_PANEL_NAME = 'pay-station-events-panel';

const initialSorting = { initialSortingState: [{ id: 'created_at', desc: true }] };

type PayStationEventsProps = {
    enableSearchParams?: boolean;
};

export default function PayStationEventsTable({ enableSearchParams = false }: PayStationEventsProps) {
    const { payStationId = '', operationId = '' } = useParams();
    const { formatMessage } = useIntl();
    const { querySortingOptions, ...sorting } = useSorting(initialSorting);
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { queryFiltersOptions, ...filters } = useFilters();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { rowSelectionState, clearSelection, setRowSelectionState } = useSingleRowSelection();
    const { parkingId } = useParking();

    const accessPayStations = useHasPermissions([PERMISSION.ACCESS_PAY_STATIONS]);
    const accessPayStationOperations = useHasPermissions([PERMISSION.ACCESS_PAY_STATION_OPERATIONS]);

    const { data, isLoading, isError } = useListPayStationEvents({
        parkingId,
        ...querySearchOptions,
        ...querySortingOptions,
        ...queryFiltersOptions,
        ...queryPaginationOptions,
        ...(payStationId && { pay_station: payStationId }),
        ...(operationId && { paystation_operation: operationId }),
    });

    const { data: payStationsData } = useListPayStations({ parkingId });

    const payStationFilterOptions = React.useMemo(
        () =>
            payStationsData?.results?.map((station) => {
                return { value: station.id, label: station?.name };
            }),
        [payStationsData],
    );

    const singleRowSelection = React.useCallback(
        (rowId: string) => setRowSelectionState({ [rowId]: true }),
        [setRowSelectionState],
    );

    const [defaultDateFilter] = React.useState(() => {
        const now = Date.now();

        return {
            afterDate: new Date(now - 30 * 24 * 60 * 60 * 1000),
            beforeDate: new Date(),
        };
    });

    const eventId = React.useMemo(() => Object.keys(rowSelectionState)[0], [rowSelectionState]);

    const columnHelper = createColumnHelper<PayStationEvent>();
    const columns = [
        columnHelper.accessor('created_at', {
            id: 'created_at',
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(paymentsMessages.date),
            },
        }),
        ...(!payStationId
            ? [
                  columnHelper.accessor('pay_station', {
                      header: DataTableColumnHeader,
                      enableHiding: true,
                      enableSorting: true,
                      enableColumnFilter: !payStationId,
                      cell: ({ row }) => {
                          return (
                              <CellLinkWithIcon
                                  toPath={`/${PATH.PAYMENTS}/pay-station/${row.original.pay_station.id}`}
                                  linkTitle={row.original.pay_station.name}
                                  hasPermissions={accessPayStations}
                              />
                          );
                      },
                      meta: {
                          title: formatMessage(paymentsMessages.payStation),
                          ...(!payStationId && {
                              options: payStationFilterOptions,
                              inputType: INPUT_TYPE.MULTI_SELECT,
                          }),
                      },
                  }),
              ]
            : []),
        columnHelper.accessor('type', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            enableColumnFilter: true,
            cell: ({ row: { original } }) => {
                const type = original.type;
                if (typeof type !== 'number') return type;
                return eventTypeTranslationMap[original.type]
                    ? formatMessage(eventTypeTranslationMap[original.type])
                    : original.type;
            },
            meta: {
                title: formatMessage(paymentsMessages.type),
                options: Object.entries(eventTypeTranslationMap).map(([key, value]) => ({
                    label: formatMessage(value),
                    value: key,
                })),
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        columnHelper.accessor('category', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(paymentsMessages.category),
                badgeOptions: eventCategoryBadgeOptions,
            },
        }),
        columnHelper.accessor('received_from', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(paymentsMessages.receivedFrom),
                badgeOptions: eventAPIBadgeOptions,
            },
        }),
        columnHelper.accessor('paystation_operation.id', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: ({ row: { original } }) => {
                const operation = original.paystation_operation;
                const id = operation?.id;
                const type = operation?.type;
                const ref = operation?.ref;

                if (!id || !type || !ref) return '-';

                const urlPath = `/${PATH.PAYMENTS}/operation/${id}/${type}`;

                return (
                    <CellLinkWithIcon toPath={urlPath} linkTitle={ref} hasPermissions={accessPayStationOperations} />
                );
            },
            meta: {
                title: formatMessage(paymentsMessages.operation),
            },
        }),
        columnHelper.accessor('created_at', {
            enableColumnFilter: true,
            meta: {
                title: formatMessage(commonMessages.time),
                inputType: INPUT_TYPE.DATE,
                dateFilter: defaultDateFilter,
            },
            id: 'timestamp',
        }),
        columnHelper.display({
            id: 'actions',
            cell: (props) => (
                <CellSidePanelActions
                    {...props}
                    sidePanelName={PAY_STATION_EVENTS_PANEL_NAME}
                    singleRowSelection={singleRowSelection}
                />
            ),
        }),
    ];

    return (
        <>
            <DataTable<PayStationEvent, any>
                isLoading={isLoading}
                isError={isError}
                columns={columns}
                data={data}
                pagination={pagination}
                sorting={sorting}
                search={search}
                filters={filters}
                enableSearchParams={enableSearchParams}
                enableLocalFilters={false}
                semantics={{
                    title: formatMessage(paymentsMessages.eventLogs),
                    entryName: formatMessage(paymentsMessages.eventLog).toLowerCase(),
                    entryNames: formatMessage(paymentsMessages.eventLogs).toLowerCase(),
                    initialColumnVisibility: {
                        created_at_range: false,
                    },
                    sidePanelName: PAY_STATION_EVENTS_PANEL_NAME,
                }}
                getRowId={(row) => row.id.toString()}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                }}
            />
            <SidePanel onClose={clearSelection} name={PAY_STATION_EVENTS_PANEL_NAME}>
                <EventSidePanel eventId={eventId} />
            </SidePanel>
        </>
    );
}
